import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Divider,
  Tooltip,
} from 'antd';
import { SearchOutlined, EditOutlined, UserAddOutlined } from '@ant-design/icons';
import { useAuth } from '../../AuthProvider';
import { baseUrl_SPB } from '../../BaseUrl';
import FetchAllEmployeesByCompany from '../../services/FetchAllEmployeesByCompany';
import EmployeeProfilePanels from './EmployeeProfilePanels';
import { fetchOnboardingStatus } from '../../services/OnboardingService';

const { Title, Text } = Typography;

const ROLE_OPTIONS = [
  { value: 'ROLE_EMPLOYEE', label: 'Employee' },
  { value: 'ROLE_FWORKER', label: 'Field Worker' },
  { value: 'ROLE_MANAGER', label: 'Manager' },
  { value: 'ROLE_CMANAGER', label: 'Company Manager' },
  { value: 'ROLE_FMANAGER', label: 'Finance Manager' },
];

const roleColour = {
  ROLE_EMPLOYEE: 'blue',
  ROLE_FWORKER: 'cyan',
  ROLE_MANAGER: 'green',
  ROLE_CMANAGER: 'purple',
  ROLE_FMANAGER: 'orange',
  ROLE_SADMIN: 'red',
};

function roleLabel(role) {
  return ROLE_OPTIONS.find((r) => r.value === role)?.label || role || '—';
}

export default function EmployeeListPage() {
  const authContext = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [onboardingSet, setOnboardingSet] = useState(new Set());

  // Edit drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [form] = Form.useForm();

  const companyId = authContext.additionalUserInfo?.companyId;
  const token = authContext.token;
  const isUserManager = authContext.role === 'ROLE_MANAGER' || authContext.role === 'ROLE_CMANAGER';

  const fetchEmployees = () => {
    if (!token) return;
    setLoading(true);
    setError('');
    FetchAllEmployeesByCompany(token)
      .then((data) => {
        setEmployees(data);
        // Check onboarding status for each employee in parallel
        Promise.all(
          data.map((emp) => {
            const id = emp.id || emp.userId || emp.employeeId;
            return fetchOnboardingStatus(token, id)
              .then((result) => (result ? emp : null))
              .catch(() => null);
          })
        ).then((results) => {
          const ids = new Set();
          results.filter(Boolean).forEach((emp) => {
            if (emp.id) ids.add(emp.id);
            if (emp.userId) ids.add(emp.userId);
            if (emp.employeeId) ids.add(emp.employeeId);
          });
          setOnboardingSet(ids);
        });
      })
      .catch(() => setError('Failed to load employees.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openEdit = (employee) => {
    setEditEmployee(employee);
    setSaveError('');
    form.setFieldsValue({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      email: employee.email || '',
      phone: employee.phoneNumber || employee.phone || '',
      role: employee.role || 'ROLE_EMPLOYEE',
      jobTitle: employee.jobTitle || '',
      department: employee.department || '',
       xeroEmployeeId: employee.xeroEmployeeId || '',
    });
    setDrawerOpen(true);
  };

  // Reusable guard for saveuser
  function canSaveUser(payload) {
    // Accepts either employee object or payload
    const id = payload?.id || payload?.userId || payload?.employeeId;
    const role = payload?.role;
    return !!id && !!role;
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setSaveError('');

      const payload = {
        ...editEmployee,
        ...values,
        companyId,
      };

      if (!canSaveUser(payload)) {
        setSaveError('Cannot save: User ID or role is missing.');
        setSaving(false);
        return;
      }

      const response = await fetch(`${baseUrl_SPB}/api/v1/user/saveuser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Save failed');

      setDrawerOpen(false);
      fetchEmployees();
    } catch (err) {
      if (err?.errorFields) return; // form validation
      setSaveError('Failed to save employee. Please try again.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const filtered = employees.filter((emp) => {
    const q = search.toLowerCase();
    if (!q) return true;
    const full = `${emp.firstName || ''} ${emp.lastName || ''} ${emp.email || ''} ${emp.role || ''} ${emp.jobTitle || ''} ${emp.department || ''}`.toLowerCase();
    return full.includes(q);
  });

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, rec) => {
        const name = `${rec.firstName || ''} ${rec.lastName || ''}`.trim() || '—';
        return name !== '—'
          ? <Typography.Text copyable={{ tooltips: ['Copy name', 'Copied!'] }}>{name}</Typography.Text>
          : '—';
      },
      sorter: (a, b) => `${a.firstName}${a.lastName}`.localeCompare(`${b.firstName}${b.lastName}`),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v) => v
        ? <Typography.Text copyable={{ tooltips: ['Copy email', 'Copied!'] }}>{v}</Typography.Text>
        : '—',
    },
    {
      title: 'Phone',
      key: 'phone',
      render: (_, rec) => {
        const phone = rec.phoneNumber || rec.phone;
        return phone
          ? <Typography.Text copyable={{ tooltips: ['Copy phone', 'Copied!'] }}>{phone}</Typography.Text>
          : '—';
      },
    },
    {
      title: 'User ID',
      key: 'userId',
      render: (_, rec) => {
        const id = rec.id || rec.userId || rec.employeeId;
        return id
          ? <Typography.Text copyable={{ tooltips: ['Copy ID', 'Copied!'] }} style={{ fontFamily: 'monospace', fontSize: 12 }}>{id}</Typography.Text>
          : '—';
      },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={roleColour[role] || 'default'}>{roleLabel(role)}</Tag>
      ),
      filters: ROLE_OPTIONS.map((r) => ({ text: r.label, value: r.value })),
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Job title',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
      render: (v) => v || '—',
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (v) => v || '—',
    },
    {
      title: 'Xero Id',
      dataIndex: 'xeroEmployeeId',
      key: 'xeroEmployeeId',
      render: (v) => v || '—',
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, rec) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEdit(rec)}
          >
            Edit
          </Button>

        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>Employees</Title>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            href="/invite"
          >
            Invite employee
          </Button>
        </Space>

        {error && <Alert type="error" showIcon message={error} />}

        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by name, email, role, job title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 420 }}
        />

        <Table
          rowKey={(rec) => rec.id || rec.email}
          dataSource={filtered}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          size="small"
          scroll={{ x: 800 }}
        />
      </Space>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editEmployee ? `Edit — ${editEmployee.firstName || ''} ${editEmployee.lastName || ''}`.trim() : 'Edit Employee'}
        width={720}
        extra={
          <Space>
            {editEmployee && onboardingSet.has(editEmployee.id || editEmployee.userId || editEmployee.employeeId) ? (
              <Button onClick={() => navigate(`/onboarding/${editEmployee.id || editEmployee.userId || editEmployee.employeeId}`)}>
                Open onboarding
              </Button>
            ) : null}
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save changes
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          {saveError && <Alert type="error" showIcon message={saveError} />}
          
          <div>
            <div style={{ marginBottom: 12 }}>
              <Typography.Title level={5} style={{ margin: 0 }}>Basic Information</Typography.Title>
            </div>
            <Form form={form} layout="vertical">
              <Form.Item name="firstName" label="First name" rules={[{ required: true, message: 'Required' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="lastName" label="Last name" rules={[{ required: true, message: 'Required' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Valid email required' }]}>
                <Input disabled />
              </Form.Item>
              <Form.Item name="phone" label="Phone number">
                <Input />
              </Form.Item>
              <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Required' }]}>
                <Select options={ROLE_OPTIONS} />
              </Form.Item>
              <Form.Item name="jobTitle" label="Job title">
                <Input placeholder="e.g. Electrician" />
              </Form.Item>
              <Form.Item name="department" label="Department">
                <Input placeholder="e.g. Operations" />
              </Form.Item>
               <Form.Item name="xeroEmployeeId" label="Xero ID">
                <Input placeholder="e.g. XERO12345" />
              </Form.Item>
            </Form>
            {editEmployee && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                ID: {editEmployee.id}
              </Text>
            )}
          </div>

          <Divider />

          <div>
            <div style={{ marginBottom: 12 }}>
              <Typography.Title level={5} style={{ margin: 0 }}>Employee Management</Typography.Title>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Manage work schedules, availability, and professional details
              </Text>
            </div>
            {editEmployee && (
              <EmployeeProfilePanels
                employee={editEmployee}
                token={token}
                onDataUpdate={fetchEmployees}
                isUserManager={isUserManager}
              />
            )}
          </div>
        </Space>
      </Drawer>
    </div>
  );
}
